import React, { useState, useMemo } from 'react';
import { Select, Row, Col, Card, Dropdown, Menu, Divider, Tooltip } from 'antd';
import styled from 'styled-components/macro';

import GlossaryRelatedTermsResult from './GlossaryRelatedTermsResult';
import { ReactComponent as MoreAction } from '../../../../images/more-vert.svg';
import AddRelatedTermsModal from './AddRelatedTermsModal';

import { PaginationBar } from '../../shared/tabs/Glossary/PaginationBar';
import { useTranslation } from '../../../../i18n/useTranslation';
import { usePagination } from '../../../shared/usePagination';
import { useEntityData } from '../../shared/EntityContext';
import { EntityType } from '../../../../types.generated';
import { useRbac } from '../../shared/useRbac';
import { ENTITY_ACTIONS } from '../../shared/rbacConstantsService';
import type { RelatedTermPrivilegeActionKey } from '../../shared/rbacConstantsService';
import { GLOSSARY_TERM, isCustomRelation, parseSelectedKey, RELATIONSHIP_TYPE_MAP, useCustomRelations } from '../utils';
import { RelatedTermTypes } from '../../shared/components/styled/search/types';

const NONE_OPTION = 'none';

const ActionButton = styled.div`
    display: flex;
    cursor: pointer;
    align-items: center;
`;

export const BrowseMenu = styled(Menu)`
    padding: 0px;
    border-radius: 4px;
    min-width: 140px !important;
`;

const styles = {
    row: { padding: '12px' },
};

// Memoize standard relation options (enum keys) - computed once
const STANDARD_RELATION_OPTIONS = Object.keys(RelatedTermTypes);

export default function GlossayRelatedTerms() {
    const { t } = useTranslation();
    const { entityData, loading } = useEntityData();
    const { canPerformRelatedTermAction } = useRbac();

    const isReadOnly = entityData?.readOnly?.readOnly ?? false;

    const parentEntityType = EntityType.GlossaryTerm;
    const getPrivilege = (action: RelatedTermPrivilegeActionKey) =>
        canPerformRelatedTermAction(parentEntityType, action);

    const canAddRelatedTerms = getPrivilege(ENTITY_ACTIONS.ADD_RELATED_TERMS);
    const canAddRelatedNodes = getPrivilege(ENTITY_ACTIONS.ADD_RELATED_NODES);
    const canRemoveRelatedTerms = getPrivilege(ENTITY_ACTIONS.REMOVE_RELATED_TERMS);
    const canRemoveRelatedNodes = getPrivilege(ENTITY_ACTIONS.REMOVE_RELATED_NODES);
    const unauthorisedTooltip = t('UNAUTHORISED_TOOLTIP');

    // Unauthorised takes precedence over read only when resolving the disabled tooltip.
    const getDisabledTooltip = (hasPrivilege: boolean) => {
        if (!hasPrivilege) return unauthorisedTooltip;
        if (isReadOnly) return t('ASSET_MARKED_AS_READ_ONLY');
        return undefined;
    };

    // Use shared hook for custom relations
    const { customRelationsMap, customRelationOptions } = useCustomRelations(GLOSSARY_TERM);

    const [isTypeTermGroup, setIsTypeTermGroup] = useState(false);
    const [selectedKey, setSelectedKey] = useState(NONE_OPTION);
    const [showAddModal, setShowAddModal] = useState(false);

    /**
     * Merge allOutgoingRelationships and allIncomingRelationships
     * and map the relationship type to the expected format
     */
    const mergedRelationships = useMemo(() => {
        if (!entityData) return [];

        // Cast entityData to any to access the new relationship fields
        const data = entityData as any;
        const outgoing = data?.allOutgoingRelationships?.relationships || [];
        const incoming = data?.allIncomingRelationships?.relationships || [];

        // Process outgoing relationships
        const processedOutgoing = outgoing.map((rel: any) => ({
            ...rel,
            isInverse: false,
            relationshipType: RELATIONSHIP_TYPE_MAP[rel.type] || rel.type,
        }));

        // Process incoming relationships (use lowercase type for mapping)
        const processedIncoming = incoming.map((rel: any) => ({
            ...rel,
            isInverse: true,
            relationshipType: RELATIONSHIP_TYPE_MAP[rel.inverseType] || rel.inverseType,
        }));

        const combinedRelationships = [...processedOutgoing, ...processedIncoming];

        // Filter out relationships that don't have a valid entity urn
        return combinedRelationships.filter((rel) => !!rel.entity?.urn && rel.type !== 'IsPartOf');
    }, [entityData]);

    // Filter relationships by selected type
    const getFilteredRelationships = () => {
        if (selectedKey === NONE_OPTION) {
            return mergedRelationships;
        }

        // Check if selected key is a custom relation (URN or URN:inverse)
        if (isCustomRelation(selectedKey)) {
            const { baseUrn, isInverseSelection } = parseSelectedKey(selectedKey);

            return mergedRelationships.filter((rel) => {
                if (rel.customRelationTypeUrn !== baseUrn) return false;
                return isInverseSelection ? rel.isInverse : !rel.isInverse;
            });
        }

        // Filter by standard relationship type
        const displayValue = RelatedTermTypes[selectedKey];
        return mergedRelationships.filter((rel) => rel.relationshipType === displayValue);
    };

    const { pageSize, page, totalCount, getItemsForPage, handleChangePage, handleShowSizeChange, resetPagination } =
        usePagination({
            items: getFilteredRelationships(),
        });

    // Handle the dropdown change
    const handleSelectChange = (value) => {
        setSelectedKey(value);
        resetPagination();
    };

    // Open the add term modal
    const handleAddTerm = () => {
        setIsTypeTermGroup(false);
        setShowAddModal(true);
    };

    // Open the add term group modal
    const handleAddTermGroup = () => {
        setIsTypeTermGroup(true);
        setShowAddModal(true);
    };

    // Get display type for the selected filter
    const getDisplayType = () => {
        if (selectedKey === NONE_OPTION) return 'All Relations';
        if (isCustomRelation(selectedKey)) {
            const { baseUrn, isInverseSelection } = parseSelectedKey(selectedKey);
            const relation = customRelationsMap[baseUrn];
            return isInverseSelection ? relation?.inverseName || selectedKey : relation?.name || selectedKey;
        }
        return RelatedTermTypes[selectedKey];
    };

    // Show the dropdown menu
    const browseMenu = (
        <BrowseMenu>
            <Tooltip title={getDisabledTooltip(canAddRelatedTerms)} overlayClassName="f-tooltip-white">
                <span>
                    <Menu.Item
                        key="add-term"
                        className="f-color-gray-s100"
                        disabled={isReadOnly || !canAddRelatedTerms}
                        onClick={handleAddTerm}
                    >
                        {t('Add Glossary Term')}
                    </Menu.Item>
                </span>
            </Tooltip>
            <Divider className="ma-0" />
            <Tooltip title={getDisabledTooltip(canAddRelatedNodes)} overlayClassName="f-tooltip-white">
                <span>
                    <Menu.Item
                        key="add-group"
                        className="f-color-gray-s100"
                        disabled={isReadOnly || !canAddRelatedNodes}
                        onClick={handleAddTermGroup}
                    >
                        {t('Add Glossary Term Group')}
                    </Menu.Item>
                </span>
            </Tooltip>
        </BrowseMenu>
    );

    return (
        <Card>
            {/* Header */}
            <Row style={styles.row} justify="space-between" align="middle">
                <Col lg={4} md={6} sm={6} xs={24}>
                    <Select
                        value={selectedKey}
                        style={{ width: '100%' }}
                        onChange={handleSelectChange}
                        placeholder={t('Select relation type')}
                        data-testid="related-terms-dropdown"
                    >
                        <Select.Option data-testid="none" key={NONE_OPTION} value={NONE_OPTION}>
                            {t('All Relations')}
                        </Select.Option>
                        {/* Standard relation options */}
                        {STANDARD_RELATION_OPTIONS.map((option) => (
                            <Select.Option data-testid={option} key={option} value={option}>
                                {RelatedTermTypes[option]}
                            </Select.Option>
                        ))}
                        {/* Custom relation options */}
                        {customRelationOptions.map((customRel) => (
                            <Select.Option data-testid={customRel.urn} key={customRel.urn} value={customRel.urn}>
                                {customRel.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col flex="none">
                    <Dropdown overlay={browseMenu} trigger={['click']}>
                        <ActionButton>
                            <MoreAction
                                width={12}
                                height={12}
                                className="f-color-gray-s100"
                                data-testid="entity-header-dropdown"
                            />
                            <span className="f-color-gray-s100 pl-1">{t('Actions')}</span>
                        </ActionButton>
                    </Dropdown>
                </Col>
            </Row>
            <Divider className="ma-0" />

            {/* Related Terms Result */}
            <Row>
                <Col xs={24}>
                    {selectedKey && entityData && (
                        <GlossaryRelatedTermsResult
                            loading={loading}
                            glossaryRelatedTermType={getDisplayType()}
                            glossaryRelatedTermResult={getItemsForPage(page)}
                            canRemoveRelatedTerms={canRemoveRelatedTerms}
                            canRemoveRelatedNodes={canRemoveRelatedNodes}
                            removeDisabledTooltip={unauthorisedTooltip}
                        />
                    )}
                </Col>
            </Row>

            {/* Pagination */}
            {totalCount > 0 && (
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    loading={loading}
                    totalCount={totalCount}
                    onChangePage={handleChangePage}
                    onShowSizeChange={handleShowSizeChange}
                />
            )}

            {/* Add Term Modal */}
            {showAddModal && (
                <AddRelatedTermsModal
                    isTypeTermGroup={isTypeTermGroup}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setShowAddModal(false)}
                />
            )}
        </Card>
    );
}
